/**
 * contracts.md §2 — the open response envelope, designed for an async future Stage 1
 * doesn't have.
 *
 * Stage 1's open is synchronous: the per-request Lambda connects, publishes, awaits the
 * `STA:` burst, and returns the result and latency in the HTTP response. Stage 2 routes
 * commands through SQS to a Fargate bridge, at which point completion is no longer
 * guaranteed inside the request.
 *
 * **The rules:**
 *
 * 1. **Stage 1 never emits `status: 'pending'`.** It is unreachable in Stage-1 code and
 *    must still be handled by every client, because an unhandled variant discovered in
 *    Stage 2 is a client release, not a server change.
 * 2. **Replay keys off the idempotency record, not off the HTTP response.** §9.2 says a
 *    replayed key returns the first stored result; that stored result is an `OpenResult`
 *    persisted server-side. A response is a *rendering* of the stored outcome, never the
 *    authority for it. **The handler and its replay path are P4's** — what lives here is
 *    the shape both stages share.
 * 3. `pollAfterMs` exists so a future async path has a defined client behaviour on day one.
 *    Stage 1 sets it never.
 */
const DELIVERY_OUTCOMES = new Set([
    'acknowledged',
    'rejected',
    'gate-acting',
    'not-acting',
    'timeout',
    'unavailable',
]);
/**
 * The one runtime check for a {@link DeliveryOutcome}, shared by the API's readers and the PWA's —
 * `isGatePosition`'s argument, one field over.
 *
 * A client hand-rolling its own list drops a member the day one is added, and *"the transport
 * reported something we don't render"* is indistinguishable on screen from *"the transport reported
 * nothing"* — which is the exact confusion D274 exists to end, arriving through the reader instead
 * of through the wire.
 */
export const isDeliveryOutcome = (value) => typeof value === 'string' && DELIVERY_OUTCOMES.has(value);
//# sourceMappingURL=open.js.map